// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { Store } from 'antd/lib/form/interface';
import { convertToLocaleId, useLocalMessage } from '@Helpers/';
import {
    PAPValueArray,
    PAPValues,
    SeverityValueArray,
    SeverityValues,
    TLPValueArray,
    TLPValues,
    CustomStatusMapType,
} from 'thehive-sdk';
import { FormInstance } from 'antd/lib/form';
import { BulkEditFormField } from '@Molecules/BulkEditFormField/BulkEditFormField';
import { SecurityInput } from '@Atoms/Inputs/SecurityInput/SecurityInput';
import { THForm } from '@Molecules/THForm/THForm';
import { THTagSelector } from '@Molecules/Inputs/THTagSelector/THTagSelector';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { CustomColorLabel } from '@Atoms/CustomColorLabel/CustomColorLabel';
import { Select } from 'antd';

const { OptGroup } = Select;

export interface CaseBulkEditValues extends Store {
    tlp: TLPValues;
    pap: PAPValues;
    severity: SeverityValues;
    addTags: string[];
    removeTags: string[];
}

interface CaseBulkEditFormProps {
    onFinish: (values: CaseBulkEditValues) => void;
    form: FormInstance;
    isValid: boolean;
    onChange: () => void;
    changeStatus?: boolean;
    caseStatus?: CustomStatusMapType;
    onCancel: () => void;
}

export const CaseBulkEditForm = ({
    onFinish,
    form,
    isValid,
    onChange,
    changeStatus = false,
    caseStatus = {},
    onCancel,
}: CaseBulkEditFormProps): ReactElement => {
    const i = useLocalMessage();

    const onValuesChange = (changedValue: Store): void => {
        const valueName = Object.keys(changedValue)[0] || '';

        if (!valueName.includes('Checked')) {
            form.setFieldsValue({ [`${valueName}Checked`]: true });
        }
        onChange();
    };

    return (
        <THForm<CaseBulkEditValues>
            name="case-bulk-edit-form"
            onValuesChange={onValuesChange}
            form={form}
            onFinish={onFinish}
            initialValues={{
                pap: 2,
                tlp: 2,
                severity: 2,
            }}
        >
            <THForm.Layout
                footer={<FormSubmitButton onCancel={onCancel} disabled={!isValid} footerText={i('general.confirm')} />}
            >
                <BulkEditFormField
                    label={i('case.create.form.tlp.label')}
                    name="tlp"
                    input={<SecurityInput type="tlp" valueArray={TLPValueArray} />}
                />

                <BulkEditFormField
                    label={i('case.create.form.pap.label')}
                    name="pap"
                    input={<SecurityInput type="pap" valueArray={PAPValueArray} />}
                />

                <BulkEditFormField
                    label={i('case.create.form.severity.label')}
                    name="severity"
                    input={<SecurityInput type="severity" hasLabel={false} valueArray={SeverityValueArray} />}
                />

                <BulkEditFormField
                    label={i('case.bulk-edit.form.add-tags.label')}
                    name="addTags"
                    input={<THTagSelector.Autocomplete />}
                />

                <BulkEditFormField
                    label={i('case.bulk-edit.form.remove-tags.label')}
                    name="removeTags"
                    input={<THTagSelector.Autocomplete />}
                />

                {changeStatus && (
                    <BulkEditFormField
                        name="status"
                        label={i('common.status')}
                        input={
                            <THSelect>
                                {Object.entries(caseStatus).map(([key, values]) => (
                                    <OptGroup key={key} label={key}>
                                        {values.map(({ value, colour }) => (
                                            <THSelect.Option key={value} value={value}>
                                                <CustomColorLabel
                                                    value={i({
                                                        id: `case.data.status.${convertToLocaleId(value)}`,
                                                        defaultMessage: value,
                                                    })}
                                                    color={colour}
                                                />
                                            </THSelect.Option>
                                        ))}
                                    </OptGroup>
                                ))}
                            </THSelect>
                        }
                    />
                )}
            </THForm.Layout>
        </THForm>
    );
};
